import { useState } from "react";
import { ChevronDown, ChevronUp, BarChart3 } from "lucide-react";

const logo = (code) => `https://a.espncdn.com/i/teamlogos/nba/500/${code?.toLowerCase()}.png`;
const pdf = (x, m, s) => Math.exp(-((x - m) ** 2) / (2 * s * s));

function Bars({ series, lo, hi, step = 2 }) {
  const bins = [];
  for (let x = lo; x <= hi; x += step) bins.push(x);
  return (
    <div className="tp-hist" data-testid="tp-hist">
      {bins.map((x) => (
        <div key={x} className="tp-hist-col" title={`${x}`}>
          {series.map((s) => <i key={s.key} className={s.cls} style={{ height: `${Math.round(pdf(x, s.mean, s.sd) * 100)}%` }} />)}
        </div>
      ))}
      <span className="tp-hist-axis"><small>{lo}</small><small>{Math.round((lo + hi) / 2)}</small><small>{hi}</small></span>
    </div>
  );
}

const VIEWS = [["win", "Win Probability"], ["score", "Projected Score"], ["range", "Score Range"], ["sim", "Simulation"]];

export function TeamProjGraphics({ g, p }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("win");
  const margin = p.home - p.away;
  return (
    <section className="tp-card tp-adv" data-testid="tp-advanced">
      <header className="tp-adv-head">
        <h3><BarChart3 size={16} />ADVANCED GRAPHICS <small>Detailed projections and simulations (optional)</small></h3>
        <button type="button" onClick={() => setOpen((v) => !v)} className="tp-btn" data-testid="tp-graphics-toggle">
          {open ? <><ChevronUp size={14} />Hide graphics</> : <><ChevronDown size={14} />Show graphics</>}
        </button>
      </header>
      {open && (
        <>
          <div className="tp-subtabs">
            {VIEWS.map(([k, t]) => <button key={k} type="button" className={view === k ? "is-active" : ""} onClick={() => setView(k)} data-testid={`tp-graphic-${k}`}>{t}</button>)}
          </div>
          <div className="tp-graphic" data-testid={`tp-graphic-view-${view}`}>
            {view === "win" && (
              <div className="tp-winbig">
                <img src={logo(g.away_code)} alt="" /><b>{Math.round(p.p_away * 100)}%</b>
                <div className="tp-split"><i style={{ width: `${p.p_away * 100}%` }} /><em /></div>
                <b>{Math.round(p.p_home * 100)}%</b><img src={logo(g.home_code)} alt="" />
              </div>
            )}
            {view === "score" && (
              <>
                <p className="tp-legend"><span className="a" />{g.away_code} {p.away}<span className="h" />{g.home_code} {p.home}</p>
                <Bars lo={Math.floor(Math.min(p.away, p.home) - 22)} hi={Math.ceil(Math.max(p.away, p.home) + 22)}
                  series={[{ key: "a", cls: "a", mean: p.away, sd: p.team_sd }, { key: "h", cls: "h", mean: p.home, sd: p.team_sd }]} />
              </>
            )}
            {view === "range" && ["away", "home"].map((s) => {
              const [lo, hi] = p.range[s];
              const min = Math.min(p.range.away[0], p.range.home[0]) - 8;
              const max = Math.max(p.range.away[1], p.range.home[1]) + 8;
              const at = (v) => `${((v - min) / (max - min)) * 100}%`;
              return (
                <div key={s} className="tp-range" data-testid={`tp-range-${s}`}>
                  <span>{g[`${s}_code`]}</span>
                  <div className={`tp-range-bar ${s}`}><i style={{ left: at(lo), width: `calc(${at(hi)} - ${at(lo)})` }} /><em style={{ left: at(p[s]) }} /></div>
                  <b>{lo} – {hi}</b>
                </div>
              );
            })}
            {view === "sim" && (
              <>
                <p className="tp-legend">Final margin ({g.home_code} − {g.away_code}) · model {margin > 0 ? "+" : ""}{margin.toFixed(1)} · {g.home_code} wins in {Math.round(p.p_home * 100)}% of projected outcomes</p>
                <Bars lo={Math.round(margin - 32)} hi={Math.round(margin + 32)} series={[{ key: "m", cls: "m", mean: margin, sd: 12.5 }]} />
              </>
            )}
          </div>
          {view === "range" && <p className="tp-note">68% likely range for each team&apos;s points.</p>}
        </>
      )}
    </section>
  );
}
